import type React from 'react';
import { useRef } from 'react';

import {
	type ChatCounterpart,
	DESK_COUNTERPART,
	type IOwnershipContext,
	type ISupportMessage,
	type SupportMessageState,
} from '../../../interface/support-chat';
import { formatFileSize, isDocumentAttachment } from '../../../lib/chat-attachment';
import { secondsUntilRetry } from '../../../lib/support-backoff';
import { tombstoneText } from '../../../lib/support-message-actions';
import { DocumentSVG } from '../../utils/svgs';
import ChatQuote from './ChatQuote';

/**
 * Delivery indicator.
 *
 * <p>State is carried by GLYPH COUNT AND FILL, never by colour alone — in dark mode
 * `--accent-600` and the success green collapse to the same value, so colour cannot
 * discriminate. Colour only reinforces.</p>
 *
 * <p>One outline tick = the server has it. Two outline = the recipient's device has it.
 * Two filled = read. If the backend cannot genuinely distinguish delivered from sent it
 * reports neither, and this shows one tick forever — showing two we cannot back up is worse
 * than showing one.</p>
 */
const DeliveryTicks = ({ state }: { state: SupportMessageState }) => {
	if (state === 'queued' || state === 'sending') {
		return (
			<span
				className='sc-ticks'
				aria-hidden='true'
			>
				⏱
			</span>
		);
	}

	if (state === 'failed') return null;

	const read = state === 'read';
	const single = state === 'sent';

	return (
		<span
			className={`sc-ticks${read ? ' sc-ticks--read' : ''}`}
			aria-hidden='true'
		>
			{single ? '✓' : '✓✓'}
		</span>
	);
};

/** Screen-reader wording for each state — the glyphs above are aria-hidden. */
const stateLabel: Record<SupportMessageState, string> = {
	queued: 'waiting to send',
	sending: 'sending',
	sent: 'sent',
	delivered: 'delivered',
	read: 'read',
	failed: 'not sent',
};

const timeOf = (iso: string) => {
	try {
		return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	} catch {
		return '';
	}
};

interface IChatMessageRow {
	message: ISupportMessage;
	own: boolean;
	/** Decides who a quote is attributed to. Defaults to a signed-out support reader. */
	ownership?: IOwnershipContext;
	/** Names the other party in the tombstone. */
	counterpart?: ChatCounterpart;
	/** Epoch ms, passed in so the countdown ticks from one timer rather than one per row. */
	now: number;
	onRetry?: (clientId: string) => void;
	/** Local object URL for an attachment still in flight, keyed by clientId. */
	localPreviewUrl?: string;
	uploadPercent?: number;
	/** Its photo was lost to a reload and must be picked again. */
	needsReattach?: boolean;
	onReattach?: (clientId: string, file: File) => void;
	/** Persisted ~4KB preview, so a reloaded bubble still shows WHICH photo is missing. */
	thumbnailDataUrl?: string;
	onOpenActions?: (messageId: string) => void;
	/**
	 * Starts a reply to this message directly. Offered on a Result Verification request card so the
	 * agent never has to discover the long-press to answer it; omitted where replies are unsupported.
	 */
	onReplyTo?: (messageId: string) => void;
}

const LEVEL_LABEL: Record<string, string> = {
	EC8A: 'Polling unit',
	EC8B: 'Ward',
	EC8C: 'LGA',
	EC8D: 'State',
};

/** Exported for tests: the unit a request is about, as the card names it. */
export const contextLabel = (context: NonNullable<ISupportMessage['context']>): string =>
	[LEVEL_LABEL[context.level] ?? 'Result', context.entityCode].filter(Boolean).join(' ');

interface IDocumentCard {
	fileName: string;
	sizeBytes: number;
	href?: string;
	uploadPercent?: number;
}

/** A PDF in a bubble. Opens in a new tab: the chat must still be there when the agent comes back. */
const DocumentCard = ({ fileName, sizeBytes, href, uploadPercent }: IDocumentCard) => {
	const meta =
		typeof uploadPercent === 'number'
			? `Sending ${uploadPercent}%`
			: ['PDF', formatFileSize(sizeBytes)].filter(Boolean).join(', ');
	const body = (
		<>
			<span className='sc-file__icon'>
				<DocumentSVG />
			</span>
			<span className='sc-file__text'>
				<span className='sc-file__name'>{fileName || 'Document.pdf'}</span>
				<span className='sc-file__meta'>{meta}</span>
			</span>
		</>
	);

	return href ? (
		<a
			className='sc-file'
			href={href}
			target='_blank'
			rel='noopener noreferrer'
			// The row is a long-press target; opening the file must not start one.
			onPointerDown={(event) => event.stopPropagation()}
			onClick={(event) => event.stopPropagation()}
			aria-label={`Open ${fileName || 'the PDF'}`}
		>
			{body}
		</a>
	) : (
		<div className='sc-file'>{body}</div>
	);
};

const ChatMessageRow = ({
	message,
	own,
	ownership = { me: 0, kind: 'support' },
	counterpart = DESK_COUNTERPART,
	now,
	onRetry,
	localPreviewUrl,
	uploadPercent,
	needsReattach,
	onReattach,
	thumbnailDataUrl,
	onOpenActions,
	onReplyTo,
}: IChatMessageRow) => {
	const pressTimer = useRef<number | null>(null);
	const pressStart = useRef<{ x: number; y: number } | null>(null);

	const clearPress = () => {
		if (pressTimer.current !== null) window.clearTimeout(pressTimer.current);
		pressTimer.current = null;
		pressStart.current = null;
	};

	// Long-press rather than hover: this is a phone held one-handed outdoors. 450ms is long
	// enough not to fire while scrolling and short enough not to feel stuck; a 10px movement
	// cancels it, because a scroll gesture starts exactly like a press.
	const startPress = (event: React.PointerEvent) => {
		if (!onOpenActions || !message.id) return;
		pressStart.current = { x: event.clientX, y: event.clientY };
		pressTimer.current = window.setTimeout(() => onOpenActions(message.id!), 450);
	};

	const movePress = (event: React.PointerEvent) => {
		if (!pressStart.current) return;
		const dx = Math.abs(event.clientX - pressStart.current.x);
		const dy = Math.abs(event.clientY - pressStart.current.y);
		if (dx > 10 || dy > 10) clearPress();
	};

	// A retracted message keeps its place in the sequence rather than closing the gap. The
	// transcript is a record; a silently reflowing one is a worse record.
	if (message.deletedAt) {
		// The tombstone keeps its place in the sequence rather than closing the gap: the
		// transcript is a record, and a silently reflowing one is a worse record.
		return (
			<div className={`sc-row sc-row--${own ? 'own' : 'other'}`}>
				<div className='sc-bubble sc-bubble--tombstone'>{tombstoneText(message.deletedBy, own, counterpart)}</div>
			</div>
		);
	}

	const interactive = !!onOpenActions && !!message.id;
	const pending = message.state === 'queued' || message.state === 'sending';
	const failed = message.state === 'failed';
	const attachment = message.attachments?.[0];
	// A PDF has no picture to draw: an <img> at it is a broken-image icon with the document behind
	// it. It gets a file card instead, and never a preview src.
	const isDocument = isDocumentAttachment(attachment);
	const previewSrc = isDocument
		? undefined
		: (localPreviewUrl ?? thumbnailDataUrl ?? attachment?.thumbnailUrl ?? attachment?.fileUrl);
	const uploading = typeof uploadPercent === 'number' && uploadPercent < 100;
	// A question from the desk about one result is a REQUEST card; the agent's own messages about it
	// carry a quieter "about" line, so the agent can see their answer landed in the right place.
	const resultContext = message.context ?? null;
	const isRequest = !!resultContext && !own;

	return (
		<div
			className={`sc-row sc-row--${own ? 'own' : 'other'}`}
			onPointerDown={startPress}
			onPointerMove={movePress}
			onPointerUp={clearPress}
			onPointerCancel={clearPress}
			onPointerLeave={clearPress}
			// Spread rather than conditional attributes: role and tabIndex are applied together
			// or not at all, so a row without actions is never a focusable non-interactive
			// element. The keyboard path exists because long-press has no equivalent.
			{...(interactive
				? {
						role: 'button' as const,
						tabIndex: 0,
						onKeyDown: (event: React.KeyboardEvent) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								onOpenActions(message.id!);
							}
						},
					}
				: {})}
		>
			<div
				className={[
					'sc-bubble',
					own ? 'sc-bubble--own' : 'sc-bubble--other',
					pending ? 'sc-bubble--pending' : '',
					needsReattach ? 'sc-bubble--reattach' : '',
					failed ? 'sc-bubble--failed' : '',
					isRequest ? 'sc-bubble--request' : '',
				]
					.filter(Boolean)
					.join(' ')}
			>
				{resultContext ? (
					<div className={isRequest ? 'sc-request__head' : 'sc-request__about'}>
						<span>{isRequest ? 'Result review' : 'About'}</span>
						<strong>{contextLabel(resultContext)}</strong>
					</div>
				) : null}

				{/* Before the photo and the body: the quote establishes what this answers, and an
				    instruction read out of order is the problem the feature exists to fix. */}
				{message.replyTo ? (
					<ChatQuote
						reply={message.replyTo}
						variant='bubble'
						ownership={ownership}
						counterpart={counterpart}
					/>
				) : null}

				{isDocument && attachment ? (
					<DocumentCard
						fileName={attachment.fileName}
						sizeBytes={attachment.fileSizeBytes}
						// Only a sent file has somewhere to open. While it is in flight the card
						// shows progress and is not a link to nothing.
						href={pending || uploading ? undefined : attachment.fileUrl}
						uploadPercent={uploading ? uploadPercent : undefined}
					/>
				) : null}

				{previewSrc ? (
					<div className='sc-attachment'>
						<img
							src={previewSrc}
							alt={attachment?.fileName ?? 'Attached photo'}
						/>
						{typeof uploadPercent === 'number' && uploadPercent < 100 ? (
							// Determinate, not a spinner. An agent deciding whether to walk toward
							// better signal needs the number, and this survives reduced-motion
							// because it is information rather than decoration.
							<div className='sc-attachment__progress'>{uploadPercent}%</div>
						) : null}
					</div>
				) : null}

				{needsReattach && message.clientId && onReattach ? (
					<div className='sc-reattach'>
						<p>This {isDocument ? 'PDF' : 'photo'} needs to be picked again — the app restarted before it finished sending.</p>
						<label className='sc-reattach__pick'>
							{isDocument ? 'Choose PDF' : 'Choose photo'}
							<input
								type='file'
								accept={isDocument ? 'application/pdf,.pdf' : 'image/*'}
								{...(isDocument ? {} : { capture: 'environment' as const })}
								hidden
								onChange={(event) => {
									const picked = event.target.files?.[0];
									if (picked) onReattach(message.clientId!, picked);
								}}
							/>
						</label>
					</div>
				) : null}

				{message.body ? <div>{message.body}</div> : null}

				{isRequest && onReplyTo && message.id ? (
					<button
						type='button'
						className='sc-request__reply'
						// The row itself is a long-press target; a tap here must not start one.
						onPointerDown={(event) => event.stopPropagation()}
						onClick={(event) => {
							event.stopPropagation();
							onReplyTo(message.id!);
						}}
					>
						Reply
					</button>
				) : null}

				<div className='sc-meta'>
					<span>{timeOf(message.createdAt)}</span>
					{message.editedAt ? <span>edited</span> : null}
					{own ? <DeliveryTicks state={message.state} /> : null}
					{/* Delivery state is the sender's information; an incoming bubble has nothing to tick. */}
					{own ? <span className='visually-hidden'>{stateLabel[message.state]}</span> : null}
				</div>
			</div>

			{failed ? (
				<div className='sc-failed-note'>
					{/* A live countdown, not a spinner. "Trying again in 45s" tells an agent the app
					    is working and to stop tapping; a bare spinner tells them nothing and they
					    tap Send four more times, which is how one stuck message becomes five. */}
					{message.clientId && onRetry ? (
						<>
							<span>Not sent</span>
							<button
								type='button'
								onClick={() => onRetry(message.clientId!)}
							>
								Retry
							</button>
						</>
					) : (
						<span>Not sent</span>
					)}
				</div>
			) : null}
		</div>
	);
};

/** Exported for the composer's countdown copy, so the wording lives in one place. */
export const retryCountdownLabel = (nextAttemptAt: number, now: number): string => {
	const seconds = secondsUntilRetry(nextAttemptAt, now);
	return seconds > 0 ? `Trying again in ${seconds}s` : 'Trying again…';
};

export default ChatMessageRow;
